import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import type { AdminCompRequest } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { looksLikeEmail } from '@/tenant/inviteGuard';
import { useTenantApi } from '@/tenant/useTenant';
import { useToast } from '@/components/useToast';

import { isSubscriptionSetupConflict, subscriberErrorMessage } from './subscriberErrors';

const UNTIL_MONTHS = [0, 3, 6, 12];

const FIELDS = ['email', 'until'] as const;

function untilFromMonths(months: number): string | undefined {
  if (months === 0) return undefined;
  const next = new Date();
  next.setMonth(next.getMonth() + months);
  return next.toISOString();
}

export interface CompDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (opts?: { quiet?: boolean }) => void;
  subscriptionId?: number;
}

export function CompDialog(props: CompDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <CompForm key={useFreshOnOpen(props.open)} {...props} />;
}

function CompForm({ open, onClose, onDone, subscriptionId }: CompDialogProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const api = useTenantApi();
  const describe = useActionError(subscriberErrorMessage);
  const emailRef = useRef<HTMLInputElement>(null);
  const untilRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [months, setMonths] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { email: emailRef, until: untilRef }, submitting);
  const existing = subscriptionId !== undefined;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!existing && !looksLikeEmail(email)) {
      setError(null);
      fields.fail({ email: t('validation.email') });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      // No end date chosen: `until` is left out (TMMAPI-0167).
      const until = untilFromMonths(months);
      const comp: AdminCompRequest = until !== undefined ? { until } : {};
      if (existing) {
        await api.comp(subscriptionId, comp);
      } else {
        await api.subscribe({ email: email.trim(), comp });
      }
      showToast(t('subscribers.toast.comped'));
      onDone();
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      if (isSubscriptionSetupConflict(err)) onDone({ quiet: true });
      fields.failMarked(mapFieldErrors(action.fieldKeys, { email: 'email', until: 'until' }), {
        email: t('subscribers.field.email'),
        until: t('subscribers.field.until'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>
          {existing ? t('subscribers.compTitle') : t('subscribers.compNewTitle')}
        </DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          {existing ? null : (
            <TextField
              required
              fullWidth
              type="email"
              margin="dense"
              label={t('subscribers.email')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                fields.clear('email');
              }}
              inputRef={emailRef}
              error={fields.errors.email !== undefined}
              helperText={fields.errors.email}
              disabled={submitting}
            />
          )}
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('subscribers.until.label')}
            value={months}
            inputRef={untilRef}
            error={fields.errors.until !== undefined}
            helperText={fields.errors.until}
            onChange={(e) => {
              setMonths(Number(e.target.value));
              fields.clear('until');
            }}
            disabled={submitting}
          >
            {UNTIL_MONTHS.map((n) => (
              <MenuItem key={n} value={n}>
                {n === 0 ? t('subscribers.until.indefinite') : t('subscribers.until.months', { n })}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('subscribers.comp')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
